import { parseBlob } from 'music-metadata';

export type BackupMp3TagSummary = {
    title: string | null;
    artist: string | null;
    album: string | null;
    durationSeconds: number | null;
    bitrateKbps: number | null;
    /** Plain text from ID3 (e.g. year or notice); display with © only when present. */
    copyright: string | null;
    /** Caller must `URL.revokeObjectURL` when discarding. */
    coverObjectUrl: string | null;
};

function joinArtists(artist?: string, artists?: string[]): string | null {
    if (artists && artists.length > 0) {
        return artists.join(', ');
    }
    if (artist?.trim()) {
        return artist.trim();
    }

    return null;
}

/**
 * Extract ID3 / MP3 tags and embedded cover from an in-memory backup file.
 */
export async function parseBackupMp3Metadata(blob: Blob): Promise<BackupMp3TagSummary> {
    const metadata = await parseBlob(blob, { duration: true });
    const { common, format } = metadata;

    let coverObjectUrl: string | null = null;
    const picture = common.picture?.[0];
    if (picture?.data?.length) {
        const mime =
            picture.format && picture.format.includes('/')
                ? picture.format
                : `image/${picture.format ?? 'jpeg'}`;
        const coverBlob = new Blob([new Uint8Array(picture.data)], { type: mime });
        coverObjectUrl = URL.createObjectURL(coverBlob);
    }

    const bitrateBps = format.bitrate;
    const bitrateKbps =
        typeof bitrateBps === 'number' && Number.isFinite(bitrateBps) ? Math.round(bitrateBps / 1000) : null;

    const durationSeconds =
        typeof format.duration === 'number' && Number.isFinite(format.duration) ? format.duration : null;

    const copyrightRaw = common.copyright;
    const copyright =
        typeof copyrightRaw === 'string' && copyrightRaw.trim() !== '' ? copyrightRaw.trim() : null;

    return {
        title: common.title?.trim() ?? null,
        artist: joinArtists(common.artist, common.artists),
        album: common.album?.trim() ?? null,
        durationSeconds,
        bitrateKbps,
        copyright,
        coverObjectUrl,
    };
}
